import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Role, RoomInfoResponse } from '@planning-session/shared';
import { getRoomInfo } from '../lib/api.js';
import { rememberedName, rememberedRole, seatToken } from '../lib/storage.js';
import { Button } from './ui/Button.js';
import styles from './JoinGate.module.css';

export interface JoinGateProps {
  readonly roomId: string;
  onJoin: (name: string, role: Role) => void;
  onBack: () => void;
}

type Lookup = { readonly state: 'loading' } | { readonly state: 'done'; readonly info: RoomInfoResponse | null };

/**
 * What someone sees when they open a shared room link: confirm the session is
 * real, then take a name. A returning tab with a seat token skips straight in.
 */
export function JoinGate({ roomId, onJoin, onBack }: JoinGateProps) {
  const [lookup, setLookup] = useState<Lookup>({ state: 'loading' });
  const [name, setName] = useState(rememberedName.get);
  const [role, setRole] = useState<Role>(rememberedRole.get);
  const [error, setError] = useState<string | null>(null);
  const autoJoined = useRef(false);

  useEffect(() => {
    let active = true;
    getRoomInfo(roomId)
      .then((info) => {
        if (active) setLookup({ state: 'done', info });
      })
      .catch(() => {
        if (active) setLookup({ state: 'done', info: null });
      });
    return () => {
      active = false;
    };
  }, [roomId]);

  const remembered = rememberedName.get();
  const hasSeat = seatToken.get(roomId) !== undefined;

  useEffect(() => {
    if (autoJoined.current || lookup.state !== 'done' || !lookup.info?.exists) return;
    if (!hasSeat || remembered.trim().length === 0) return;

    autoJoined.current = true;
    onJoin(remembered.trim(), rememberedRole.get());
  }, [lookup, hasSeat, remembered, onJoin]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('Add your name so the room knows who is voting.');
      return;
    }

    rememberedName.set(trimmed);
    rememberedRole.set(role);
    onJoin(trimmed, role);
  }

  if (lookup.state === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.panel}>
          <h1 className={styles.heading}>Looking for this session</h1>
          <p className={styles.codeInline}>{roomId}</p>
        </div>
      </div>
    );
  }

  if (!lookup.info?.exists) {
    return (
      <div className={styles.page}>
        <div className={styles.panel}>
          <h1 className={styles.heading}>Session not found</h1>
          <p className={styles.codeInline}>{roomId}</p>
          <p className={styles.meta}>
            This session has ended or never existed. Sessions are cleared a couple of hours after everyone leaves.
          </p>
          <Button variant="primary" size="large" block onClick={onBack}>
            Start a new session
          </Button>
        </div>
      </div>
    );
  }

  const { participantCount } = lookup.info;

  return (
    <div className={styles.page}>
      <form className={styles.panel} onSubmit={handleSubmit}>
        <h1 className={styles.code}>{roomId}</h1>
        <p className={styles.meta}>
          {participantCount === 0
            ? 'You will be the first one here.'
            : `${participantCount} ${participantCount === 1 ? 'person is' : 'people are'} already here.`}
        </p>

        <label className={styles.field}>
          <span className={styles.label}>Your name</span>
          <input
            className={styles.input}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Ada Lovelace"
            autoComplete="nickname"
            maxLength={40}
            autoFocus
          />
        </label>

        <div className={styles.field}>
          <span className={styles.label}>Join as</span>
          <div className={styles.roles} role="group" aria-label="Join as">
            {(['player', 'observer'] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={`${styles.role} ${role === option ? styles.roleActive : ''}`}
                aria-pressed={role === option}
                onClick={() => setRole(option)}
              >
                {option === 'player' ? 'Player' : 'Observer'}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <Button type="submit" variant="primary" size="large" block>
            Take a seat
          </Button>
          <Button variant="ghost" size="small" block onClick={onBack}>
            Back
          </Button>
        </div>
      </form>
    </div>
  );
}
